<script lang="ts">
	import {
		Button,
		Modal,
		Container,
		Text,
		Input,
		Wizard,
		WizardItem,
		WizardContent,
		WizardContainer,
	} from '@Shelly/shelly-design-system';
	import { applicationHostUrl } from 'src/helpers/account_helpers';
	import { openExternalLink } from 'src/helpers/utilities';
	import { HttpClient } from 'src/services/utils/http-client';
	import { createEventDispatcher, onMount } from 'svelte';
	import { format } from 'svelte-i18n';
	import { push } from 'svelte-spa-router';
	import { ErrorCode, type ErrorScreen } from './errorVoucherCodes';
	import { user_data, user_settings } from 'src/stores/account/user';
	import SetUpWeatherLocation from '../Weather/SetUpWeatherLocation.svelte';
	import type {
		RegWeatherServiceResponse,
		WeatherStationSaveData,
	} from 'src/types/requests/services/weather';
	import { addWeatherStation } from 'src/helpers/geolocalion_helper';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { getVoucherUrl } from 'src/helpers/user_helpers';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let openVoucherModal = false;
	export let qrVoucherCode = '';
	export let paypalInfo;

	const dispatch = createEventDispatcher();
	let weatherStationSaveData: WeatherStationSaveData = $user_settings.weather_station?.data;
	let voucherCode = qrVoucherCode ?? '';
	let isButtonDisabled = false;
	let isActivated = false;
	let voucherSubmitted = false;

	const urlToUse = getVoucherUrl();
	let formattedError = '';
	let serverError = null;
	let errorData = null;
	let uiMode: UiMode = 5;
	let setHight100Class = false;

	const removeQueryString = () => {
		push('/settings/premium');
	};

	const isVoucherCodeFormatted = (code) => {
		let isValid = false;
		formattedError = '';

		if (!code) {
			formattedError = $format('error_voucher_empty', {
				default: 'Voucher code cannot be empty.',
			});
		} else if (code.length < 8) {
			formattedError = $format('error_voucher_limit', {
				default: 'Voucher code must be at least 8 characters long.',
			});
		} else if (/[^a-zA-Z0-9]/.test(code)) {
			formattedError = $format('error_voucher_invalid', {
				default:
					'Voucher code can only contain letters and numbers. No spaces or special characters allowed.',
			});
		}

		isValid = formattedError === '';
		return isValid;
	};

	let wizardItems = [
		{
			active: true,
			activeIndex: 0,
			disabled: false,
			showMoreDots: false,
			stepNumber: 1,
			title: $format('welcome_message'),
		},
		{
			active: false,
			activeIndex: 1,
			disabled: false,
			showMoreDots: false,
			stepNumber: 2,
			title: $format('set_up_weather_location'),
		},
	];
	let activeStep = wizardItems[0];
	$: isLastStepWizard = activeStep.activeIndex + 1 >= wizardItems.length;

	const onClickWizardItem = (e, index: number) => {
		wizardItems = wizardItems.map((item, i) => {
			if (i === index) {
				activeStep = {
					...item,
					activeIndex: i,
				};

				return {
					...item,
					active: true,
				};
			}

			return {
				...item,
				active: false,
			};
		});
	};

	onMount(() => {
		if (qrVoucherCode) {
			handleVoucherCodeVerification();
		}
		removeQueryString();
	});

	enum UiMode {
		ManualVaucherForm,
		ValidVoucherActiveSubscription,
		ValidVoucherNoActiveSubscription,
		InvalidVoucher,
		ActivationVoucherActiveSubscription,
		ActivationVoucherNoActiveSubscription,
		AlreadyHaveActiveVouchers,
	}

	const handleActivationResponse = () => {
		if (serverError !== null && serverError !== undefined) {
			uiMode = UiMode.InvalidVoucher;
		} else if (paypalInfo.subscriptionStatus === 'ACTIVE') {
			uiMode = isActivated
				? UiMode.ActivationVoucherActiveSubscription
				: UiMode.ValidVoucherActiveSubscription;
		} else {
			uiMode = isActivated
				? paypalInfo?.whatIsHappening?.hasVouchers
					? UiMode.AlreadyHaveActiveVouchers
					: UiMode.ActivationVoucherNoActiveSubscription
				: UiMode.ValidVoucherNoActiveSubscription;
		}
	};

	$: {
		if (qrVoucherCode) {
			if (voucherSubmitted) {
				handleActivationResponse();
			}
		} else if (openVoucherModal) {
			if (voucherCode) {
				if (voucherSubmitted) {
					const isVoucherFormatValid = isVoucherCodeFormatted(voucherCode);
					if (!isVoucherFormatValid) {
						uiMode = UiMode.ManualVaucherForm;
					} else {
						handleActivationResponse();
					}
				}
			} else {
				uiMode = UiMode.ManualVaucherForm;
				// uiMode = 5;
			}
		}
		// console.log({
		// 	voucherCode,
		// 	openVoucherModal,
		// 	qrVoucherCode,
		// 	formattedError,
		// 	isActivated,
		// 	paypalInfo,
		// 	voucherSubmitted,
		// 	serverError,
		// 	uiMode,
		// 	errorData,
		// });
	}

	// Use to set correct size for the map, after the step with map is active
	$: if (activeStep.activeIndex === 1) {
		setTimeout(() => {
			setHight100Class = true;
			// Time before step transition duration end
		}, 400);
	} else {
		setHight100Class = false;
	}

	// TODO: R.K - > move this in a more appropriate place, maybe a separate file, once i figure out why translations are breaking
	let ErrorTemplateMap: Record<ErrorCode, ErrorScreen>;
	$: ErrorTemplateMap = {
		[ErrorCode.USED_CODE]: {
			screen: 1,
			image: './images/premium/invalid_voucher.png',
			title: $format('error_used_voucher', {
				default: 'This voucher has already been used.',
			}),
			description: $format('error_voucher_userd', {
				default: `The voucher code {voucherCode} has already beedn used. Each voucher can only be redeemed once.`,
				values: { voucherCode },
			}),
			span: $format('error_if_issue_persists_v2', {
				default:
					'If you beleive this is an error, or if you have questions, please contact our support team.',
			}),
		},
		[ErrorCode.NOT_EXISTING]: {
			screen: 2,
			image: './images/premium/invalid_voucher.png',
			title: $format('error_invalid_voucher', {
				default: 'Invalid voucher code.',
			}),
			description: $format('error_invalid_voucher_text', {
				default:
					'The voucher code {voucherCode} you entered is not recognized or is invalid. Please double-check the code on your voucher card and try again. Ensure you have scratched off the protective layer completely to reveal the correct code.',
				values: { voucherCode },
			}),
			span: $format('error_if_issue_persists', {
				default: 'If the issue persists, please ',
			}),
		},
		[ErrorCode.INVALID_INPUT_CODE]: {
			screen: 2,
			image: './images/premium/invalid_voucher.png',
			title: $format('error_invalid_voucher', {
				default: 'Invalid voucher code.',
			}),
			description: $format('error_invalid_voucher_text', {
				default:
					'The voucher code {voucherCode} you entered is not recognized or is invalid. Please double-check the code on your voucher card and try again. Ensure you have scratched off the protective layer completely to reveal the correct code.',
				values: { voucherCode },
			}),
			span: $format('error_if_issue_persists', {
				default: 'If the issue persists, please ',
			}),
		},
		[ErrorCode.INVALID_CODE]: {
			screen: 2,
			image: './images/premium/invalid_voucher.png',
			title: $format('error_invalid_voucher', {
				default: 'Invalid voucher code.',
			}),
			description: $format('error_invalid_voucher_text', {
				default: `The voucher code ${voucherCode} you entered is not recognized or is invalid. Please double-check the code on your voucher card and try again. Ensure you have scratched off the protective layer completely to reveal the correct code.`,
				values: { voucherCode },
			}),
			span: $format('error_if_issue_persists', {
				default: 'If the issue persists, please ',
			}),
		},
		[ErrorCode.EXPIRED_CODE]: {
			screen: 3,
			image: '/images/premium/invalid_voucher_unable.png',
			title: $format('error_unable_to_validate_voucher', {
				default: 'Unable to validate voucher',
			}),
			description: $format('error_unable_to_validate_voucher_text', {
				default:
					'Sorry, we couldn not validate your voucher at this time. Please try again in a few moments.',
			}),
			span: $format('error_if_issue_persists', {
				default: 'If the issue persists, please ',
			}),
		},
		[ErrorCode.NOT_BEFORE]: {
			screen: 3,
			image: './images/premium/invalid_voucher_unable.png',
			title: $format('error_unable_to_validate_voucher', {
				default: 'Unable to validate voucher',
			}),
			description: $format('error_unable_to_validate_voucher_text', {
				default:
					'Sorry, we couldn not validate your voucher at this time. Please try again in a few moments.',
			}),

			span: $format('error_if_issue_persists', {
				default: 'If the issue persists, please ',
			}),
		},
		[ErrorCode.NOT_AFTER]: {
			screen: 3,
			image: './images/premium/invalid_voucher_unable.png',
			title: $format('error_unable_to_validate_voucher', {
				default: 'Unable to validate voucher',
			}),
			description: $format('error_unable_to_validate_voucher_text', {
				default:
					'Sorry, we couldn not validate your voucher at this time. Please try again in a few moments.',
			}),

			span: $format('error_if_issue_persists', {
				default: 'If the issue persists, please ',
			}),
		},
		[ErrorCode.ALREADY_HAS_COUPON]: {
			screen: 4,
			image: './images/premium/invalid_voucher.png',
			title: $format('already_has_voucher_error_msg', {
				default: 'Already has an active voucher',
			}),
			description: $format('already_has_voucher_error_text', {
				default:
					'An active voucher is already associated with your account. You can only have one active voucher at a time.',
			}),

			span: $format('error_if_issue_persists', {
				default: 'If you have any questions, please ',
			}),
			hideRetry: true,
		},
		[ErrorCode.MAX_NUMBER_OF_COUPONS_REACHED]: {
			screen: 4,
			image: './images/premium/invalid_voucher.png',
			title: $format('title_max_active_vouchers_reached', {
				default: 'Maximum active vouchers reached',
			}),
			description: $format('description_max_active_vouchers_reached', {
				default: 'You have already reached the maximum number of active vouchers.',
			}),

			span: $format('error_if_issue_persists', {
				default: 'If you have any questions, please ',
			}),
			hideRetry: true,
		},
	};

	const saveStation = async () => {
		const res: RegWeatherServiceResponse = await addWeatherStation(weatherStationSaveData);

		if (res?.isok) {
			onSettingsResponse(res);
			dispatch('close');
		} else {
			onSettingsResponse(res);
		}
	};

	export const handleVoucherCodeVerification = () => {
		let code = voucherCode.trim();

		if (isVoucherCodeFormatted(code)) {
			let httpCl = new HttpClient(urlToUse);
			httpCl.setAuthToken($user_data.token);

			httpCl
				.get('/coupons/check_code_status?code=' + code)
				.then((response) => {
					console.log('Response:', response);
					voucherSubmitted = true;
				})
				.catch((error) => {
					console.error('Error:', error.response);
					serverError = error.response.data.reason;
					errorData = ErrorTemplateMap[serverError];
					uiMode = UiMode.InvalidVoucher;
				})
				.finally(() => {});
		} else {
			isButtonDisabled = true;
		}
	};

	export const handleVoucherCodeActivation = async () => {
		await handleVoucherCodeVerification();

		if (!isEmailValidated) {
			dispatch('noEmailVerified');
			return true;
		}

		let httpCl = new HttpClient(urlToUse);
		httpCl.setAuthToken($user_data.token);

		httpCl
			.get('/coupons/activate?code=' + voucherCode)
			.then((response) => {
				console.log('Response:', response);
				isActivated = true;
			})
			.catch((error) => {
				console.error('Error:', error.response);
				serverError = error.response.data.reason;
				errorData = ErrorTemplateMap[serverError];
			})
			.finally(() => {
				handleActivationResponse();
			});
	};

	$: isEmailValidated = $user_settings.mail_validated;
</script>

<Modal
	showModal={openVoucherModal}
	on:close={() => {
		setHight100Class = false;
		dispatch('close');
	}}
	title={$format('voucher_code_activation', {
		default: 'Premium Voucher Activation',
	})}
	removeScrollPadding
	hideSlotFooter={uiMode === UiMode.ValidVoucherActiveSubscription ||
		uiMode === UiMode.ValidVoucherNoActiveSubscription}
>
	<svelte:fragment slot="content">
		{#if uiMode === UiMode.ManualVaucherForm}
			<Container padding="scrollPaddings">
				<div class="d-flex align-items-center flex-direction-column justify-content-center">
					<img
						class="max-width-full"
						alt="premium voucher"
						src="./images/premium/present_ilustration.png"
					/>
				</div>

				<Text element="h1" class="text-center text-normal mt-m mb-s "
					>{$format('enter_voucher_code', {
						default: 'Enter Your Voucher Code',
					})}</Text
				>

				<Text element="p" class="text-center text-normal  body-small-styles text-color-muted "
					>{$format('enter_voucher_code_prompt', {
						default: 'Please enter the Premium activation code found on your voucher card.',
					})}
				</Text>
				<Text element="p" class="text-center text-normal body-small-styles text-color-muted mb-m"
					>{$format('voucher_code_placement', {
						default: 'You can find the code under the scratch-off area.',
					})}
				</Text>

				<Input
					aria-label={$format('input_voucher_code', { default: 'Input Voucher Code' })}
					type="text"
					icon="fas fa-search"
					size="m"
					error={formattedError}
					on:focus={() => {
						formattedError = '';
						isButtonDisabled = false;
						voucherSubmitted = false;
					}}
					bind:value={voucherCode}
					id="input-hu6g653"
				/>

				<div class="text-center mt-m">
					<span class="mt-m text-center text-small body-small-styles"
						>{$format('part_inline_link', { default: 'View the full' })}
					</span>
					<span
						class="link-styles text-center text-small"
						style="font-size: inherit; line-height: inherit;"
						role="presentation"
						on:click={() => push(`/terms-and-conditions`)}
					>
						{$format('terms', { default: 'Terms and conditions' })}
					</span>
					<span class="mt-m text-center text-small body-small-styles"
						>{$format('part_inline_link_voucher', {
							default: 'for Premium voucher.',
						})}
					</span>
				</div>
			</Container>
		{/if}

		{#if uiMode === UiMode.ValidVoucherActiveSubscription}
			<Container padding="scrollPaddings">
				<Container padding="scrollPaddings">
					<div class="d-flex align-items-center flex-direction-column justify-content-center">
						<img
							class="max-width-full"
							alt="premium voucher"
							src="./images/premium/present_ilustration.png"
						/>
					</div>

					<Text element="h1" class="text-center text-normal mt-m mb-s "
						>{$format('voucher_extend_12_month', {
							default: 'Extend Current Plan with 12-month voucher',
						})}</Text
					>

					<Text
						element="p"
						class="text-center text-normal  body-small-styles text-color-muted "
						>{$format('voucher_12_months_extention', {
							default:
								'Activating this 12-month voucher with code {voucherCode} will extend your current subscription.',
							values: { voucherCode },
						})}
					</Text>

					<div
						class="d-flex align-items-center flex-direction-column justify-content-center mt-m"
					>
						<Button on:click={handleVoucherCodeActivation}>
							{$format('voucher_confirmation_1', {
								default: 'Yes, activate voucher',
							})}
						</Button>

						<div
							class="link-styles text-center text-small mt-m"
							role="presentation"
							on:click={() => {
								dispatch('close');
							}}
						>
							{$format('maybe_later', { default: 'Maybe later' })}
						</div>
					</div>

					<div class="text-center mt-m">
						<span class="mt-m text-center text-small body-small-styles"
							>{$format('part_inline_link', { default: 'View the full' })}
						</span>
						<span
							class="link-styles text-center text-small"
							style="font-size: inherit; line-height: inherit;"
							role="presentation"
							on:click={() => push(`/terms-and-conditions`)}
						>
							{$format('terms', { default: 'Terms and conditions' })}
						</span>
						<span class="mt-m text-center text-small body-small-styles"
							>{$format('part_inline_link_voucher', {
								default: 'for Premium voucher.',
							})}
						</span>
					</div>
				</Container>
			</Container>
		{/if}

		{#if uiMode === UiMode.ValidVoucherNoActiveSubscription}
			<Container padding="scrollPaddings">
				<Container padding="scrollPaddings">
					<div class="d-flex align-items-center flex-direction-column justify-content-center">
						<img
							class="max-width-full"
							alt="premium voucher"
							src="./images/premium/present_ilustration.png"
						/>
					</div>

					<Text element="h1" class="text-center text-normal mt-m mb-s "
						>{$format('voucher_activate_text', {
							default: 'Activate your Premium voucher',
						})}</Text
					>

					<Text
						element="p"
						class="text-center text-normal  body-small-styles text-color-muted "
					>
						{$format('voucher_activation_notice_12_months', {
							default:
								"You're about to activate a 12-month Shelly Premium voucher with code {voucherCode}.",
							values: { voucherCode },
						})}
					</Text>

					<div
						class="d-flex align-items-center flex-direction-column justify-content-center mt-m"
					>
						<Button on:click={handleVoucherCodeActivation}>
							{$format('confirm_activation', { default: 'Confirm Activation' })}
						</Button>

						<div
							class="link-styles text-center text-small mt-m"
							role="presentation"
							on:click={() => {
								dispatch('close');
							}}
						>
							{$format('maybe_later', { default: 'Maybe later' })}
						</div>
					</div>

					<div class="text-center mt-m">
						<span class="mt-m text-center text-small body-small-styles"
							>{$format('part_inline_link', { default: 'View the full' })}
						</span>
						<span
							class="link-styles text-center text-small"
							style="font-size: inherit; line-height: inherit;"
							role="presentation"
							on:click={() => push(`/terms-and-conditions`)}
						>
							{$format('terms', { default: 'Terms and conditions' })}
						</span>
						<span class="mt-m text-center text-small body-small-styles"
							>{$format('part_inline_link_voucher', {
								default: 'for Premium voucher.',
							})}
						</span>
					</div>
				</Container>
			</Container>
		{/if}

		{#if uiMode === UiMode.InvalidVoucher}
			<Container padding="scrollPaddings">
				<Container padding="scrollPaddings">
					<div class="d-flex align-items-center flex-direction-column justify-content-center">
						<img class="max-width-full" alt="premium voucher" src={errorData.image} />
					</div>

					<Text element="h1" class="text-center text-normal mt-m mb-s ">{errorData.title}</Text
					>

					<Text
						element="p"
						class="text-center text-normal  body-small-styles text-color-muted "
						>{errorData.description}
					</Text>

					<div class="text-center mt-m">
						<span class="text-center text-normal body-small-styles text-color-muted mb-m"
							>{errorData.span}
						</span>
						<span
							class="link-styles text-center text-small"
							style="font-size: inherit; line-height: inherit;"
							role="presentation"
							on:click={(e) => {
								e.preventDefault();
								openExternalLink(applicationHostUrl('https://support.shelly.cloud/'));
							}}
						>
							{$format('voucher_end_contant_support_team', {
								default: 'contact our support team.',
							})}
						</span>
					</div>
				</Container>
			</Container>
		{/if}

		{#if uiMode === UiMode.ActivationVoucherActiveSubscription || uiMode === UiMode.AlreadyHaveActiveVouchers}
			<Container padding="scrollPaddings">
				<Container padding="scrollPaddings">
					<div class="d-flex align-items-center flex-direction-column justify-content-center">
						<img
							class="max-width-full"
							alt="premium voucher"
							src="./images/premium/activation_complete.png"
						/>
					</div>

					<Text element="h1" class="text-center text-normal mt-m mb-s "
						>{$format('voucher_activation_successful', {
							default: 'Congradulations, your Shelly Premium is extended!',
						})}
					</Text>

					<Text
						element="p"
						class="text-center text-normal  body-small-styles text-color-muted "
						>{$format('voucher_activation_successfull_12_months', {
							default:
								'Your Shelly Premium is extended with 12 months from voucher with code {voucherCode}.',
							values: { voucherCode },
						})}
					</Text>
				</Container>
			</Container>
		{/if}

		{#if uiMode === UiMode.ActivationVoucherNoActiveSubscription}
			<Container padding="scrollPaddings" slot="toolbar">
				<Wizard class="mb-m">
					{#each wizardItems as item, i (i)}
						<WizardItem
							{...item}
							on:click={(e) => {
								onClickWizardItem(e, i);
							}}
							indicatorColor={$showNewLayout ? 'color-2' : undefined}
						/>
					{/each}
				</Wizard>
			</Container>
			<WizardContainer
				class="max-height-100"
				activeStepIndex={activeStep.activeIndex}
				adaptiveHeight
			>
				{#each wizardItems as item, i (i)}
					<WizardContent
						active={activeStep.activeIndex === i}
						class={classMap({
							'max-height-100': setHight100Class && activeStep.activeIndex === 1,
						})}
					>
						{#if i === 0}
							<Container padding="scrollPaddings">
								<Container padding="scrollPaddings">
									<div
										class="d-flex align-items-center flex-direction-column justify-content-center"
									>
										<img
											class="max-width-full"
											alt="premium voucher"
											src="./images/premium/activation_complete.png"
										/>
									</div>

									<Text element="h1" class="text-center text-normal mt-m mb-s "
										>{$format('voucher_is_premium', {
											default: 'Congradulations, you are now a Premium User!',
										})}
									</Text>

									<Text
										element="p"
										class="text-center text-normal  body-small-styles text-color-muted "
									>
										{$format('voucher_activation_successfull_12_months_2', {
											default:
												'Your 12-month Shelly Premium period from voucher with code {voucherCode} is now active. Enjoy your premium benefits.',
											values: { voucherCode },
										})}
									</Text>
								</Container>
							</Container>
						{:else}
							<SetUpWeatherLocation
								bind:weatherStationSaveData
								mapParentSize
								resizeMap={setHight100Class && activeStep.activeIndex === 1}
								on:open={() => (setHight100Class = false)}
							/>
						{/if}
					</WizardContent>
				{/each}
			</WizardContainer>
		{/if}
	</svelte:fragment>

	<svelte:fragment slot="footer">
		{#if uiMode === UiMode.ManualVaucherForm}
			<div class="d-flex justify-content-end">
				<Button
					variant="secondary"
					class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
					responsiveSize={{
						sm: 'm',
					}}
					on:click={() => {
						dispatch('close');
					}}
				>
					{$format('button_cancel', { default: 'Cancel' })}
				</Button>
				<Button
					class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
					responsiveSize={{
						sm: 'm',
					}}
					on:click={handleVoucherCodeVerification}
					disabled={isButtonDisabled}
				>
					{$format('voucher_confirmation_2', { default: 'Activate Voucher' })}
				</Button>
			</div>
		{/if}
		{#if uiMode === UiMode.InvalidVoucher}
			<div class="d-flex align-items-center flex-direction-column justify-content-center">
				{#if !errorData?.hideRetry}
					<Button
						on:click={() => {
							// to-do -> if comes from qr code scan again, if comes from link open modal with manual input
							voucherSubmitted = false;
							formattedError = '';
							isButtonDisabled = false;
							serverError = null;
							errorData = null;
							uiMode = UiMode.ManualVaucherForm;
						}}
					>
						{$format('voucher_enter_code_again', {
							default: 'Enter code again',
						})}
					</Button>
					<div
						class="link-styles text-center text-small mt-m"
						role="presentation"
						on:click={() => {
							dispatch('close');
						}}
					>
						{$format('button_cancel', { default: 'Cancel' })}
					</div>
				{:else}
					<Button
						type="primary"
						on:click={() => {
							dispatch('close');
						}}>{$format('button_ok', { default: 'Ok' })}</Button
					>
				{/if}
			</div>
		{/if}

		{#if uiMode === UiMode.ActivationVoucherActiveSubscription || uiMode === UiMode.AlreadyHaveActiveVouchers}
			<div class="d-flex align-items-center flex-direction-column justify-content-center">
				<Button
					on:click={() => {
						dispatch('close');
					}}
				>
					{$format('ok_got_it', { default: 'Okay, got it' })}
				</Button>
			</div>
		{/if}

		{#if uiMode === UiMode.ActivationVoucherNoActiveSubscription}
			<div class="d-flex align-items-end flex-direction-row justify-content-end">
				<Button
					variant="secondary"
					width="s"
					disabled={activeStep.activeIndex == 0}
					class="mr-s"
					on:click={() => {
						if (activeStep.activeIndex <= 0) {
							return;
						}
						onClickWizardItem({}, activeStep.activeIndex - 1);
					}}
				>
					{$format('button_back', { default: 'Back' })}
				</Button>

				<Button
					id="wizard-next-button"
					aria-labelledby="wizard-next-button"
					width={isLastStepWizard ? 'm' : 'l'}
					class="ml-s"
					disabled={!weatherStationSaveData?.stationID && isLastStepWizard}
					on:click={() => {
						if (isLastStepWizard) {
							saveStation();
						}

						onClickWizardItem({}, activeStep.activeIndex + 1);
					}}
				>
					{#if isLastStepWizard}
						{$format('button_save')}
					{:else}
						{$format('button_next', { default: 'Next' })}
					{/if}
				</Button>
			</div>
		{/if}
	</svelte:fragment>
</Modal>
